import { useEffect, useMemo, useState } from 'react'
import Modal from 'components/modal/Modal'
import './createPostModal.css'

const buildEmptyPost = () => ({
  desc: '',
  imageFile: null,
  imagePreview: null,
  existingImageUrl: null,
  pollQuestion: '',
  pollOptions: ['', ''],
  pollMultiple: false,
})

const buildPostFromInitial = (initialPost) => ({
  desc: initialPost.desc || '',
  imageFile: null,
  imagePreview: null,
  existingImageUrl: initialPost.payload?.image?.url || null,
  pollQuestion: initialPost.payload?.poll?.name || '',
  pollOptions: initialPost.payload?.poll?.options?.length
    ? initialPost.payload.poll.options.map((option) => option.name)
    : ['', ''],
  pollMultiple: !!initialPost.payload?.poll?.multiple,
})

const toBase64 = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader()
  reader.onload = () => resolve(reader.result)
  reader.onerror = reject
  reader.readAsDataURL(file)
})

const CreatePostModal = ({ isModalOpen, onClose, createPost, uploadImage, initialPost }) => {

  const isEditing = !!initialPost

  const [post, setPost] = useState(buildEmptyPost())
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (isModalOpen) {
      setPost(initialPost ? buildPostFromInitial(initialPost) : buildEmptyPost())
    }
  }, [isModalOpen])

  const hasPollQuestion = post.pollQuestion.trim().length > 0
  const filledPollOptions = post.pollOptions.filter((option) => option.trim().length > 0)

  const isDisabled = useMemo(() => {
    if (loading) return true
    if (!post.desc.trim()) return true
    if (hasPollQuestion && filledPollOptions.length < 2) return true
    return false
  }, [loading, post.desc, hasPollQuestion, filledPollOptions.length])

  const reset = () => {
    setPost(buildEmptyPost())
  }

  const handleImageChange = (e) => {
    const file = e.target.files[0]
    if (!file || !file.type.startsWith('image/')) return
    setPost((prev) => ({ ...prev, imageFile: file, imagePreview: URL.createObjectURL(file) }))
  }

  const removeImage = () => {
    setPost((prev) => ({ ...prev, imageFile: null, imagePreview: null, existingImageUrl: null }))
  }

  const handlePollOptionChange = (index, value) => {
    setPost((prev) => {
      const pollOptions = [...prev.pollOptions]
      pollOptions[index] = value
      return { ...prev, pollOptions }
    })
  }

  const addPollOption = () => {
    setPost((prev) => ({ ...prev, pollOptions: [...prev.pollOptions, ''] }))
  }

  const removePollOption = (index) => {
    setPost((prev) => {
      const pollOptions = [...prev.pollOptions]
      pollOptions.splice(index, 1)
      return { ...prev, pollOptions }
    })
  }

  const submit = async () => {
    try {
      setLoading(true)

      const payload = {}

      if (post.imageFile) {
        const base64 = await toBase64(post.imageFile)
        const { url } = await uploadImage(base64)
        payload.image = { url }
      } else if (post.existingImageUrl) {
        payload.image = { url: post.existingImageUrl }
      }

      if (hasPollQuestion) {
        payload.poll = {
          name: post.pollQuestion.trim(),
          options: filledPollOptions.map((name) => ({ name })),
          multiple: post.pollMultiple,
        }
      }

      await createPost({ desc: post.desc, payload })
      reset()
      onClose()
    } catch (e) {
      console.error(e)
    } finally {
      setLoading(false)
    }
  }

  const handleClose = () => {
    reset()
    onClose()
  }

  const imageToShow = post.imagePreview || post.existingImageUrl

  return (
    <Modal isOpen={isModalOpen} onClose={handleClose} closeButton width='800px'>
      <h2 className='modal_title'>{isEditing ? 'Modifier le post' : 'Créer un nouveau post'}</h2>
      <div className='new_post_div'>
        <div className='new_post_vertical_div'>
          <p><strong>Contenu :</strong></p>
          <textarea
            className='create_post_textarea'
            value={post.desc}
            onChange={(e) => setPost((prev) => ({ ...prev, desc: e.target.value }))}
          />
        </div>

        <div className='new_post_vertical_div'>
          <p><strong>Image :</strong></p>
          {imageToShow ? (
            <div className='create_post_image_preview_box'>
              <img className='create_post_image_preview' src={imageToShow} alt='' />
              <span className='pointer create_post_remove_image' onClick={removeImage}>Retirer l'image</span>
            </div>
          ) : (
            <input type='file' accept='image/*' onChange={handleImageChange} />
          )}
        </div>

        <div className='new_post_vertical_div'>
          <p><strong>Sondage :</strong></p>
          <input
            type='text'
            placeholder='Question du sondage'
            className='create_post_text_input_large'
            value={post.pollQuestion}
            onChange={(e) => setPost((prev) => ({ ...prev, pollQuestion: e.target.value }))}
          />
          {hasPollQuestion && (
            <div className='create_post_poll_options'>
              {post.pollOptions.map((option, index) => (
                <div key={index} className='create_post_poll_option_row'>
                  <input
                    type='text'
                    placeholder={`Réponse ${index + 1}`}
                    className='create_post_text_input_large'
                    value={option}
                    onChange={(e) => handlePollOptionChange(index, e.target.value)}
                  />
                  {post.pollOptions.length > 2 && (
                    <span className='pointer create_post_remove_option' onClick={() => removePollOption(index)}>✕</span>
                  )}
                </div>
              ))}
              <span className='pointer create_post_add_option' onClick={addPollOption}>+ Ajouter une réponse</span>
              <label className='create_post_checkbox_label'>
                <input
                  type='checkbox'
                  checked={post.pollMultiple}
                  onChange={(e) => setPost((prev) => ({ ...prev, pollMultiple: e.target.checked }))}
                />
                Autoriser plusieurs réponses
              </label>
            </div>
          )}
        </div>
      </div>
      <hr className='modal_hr_button' />
      <div className='modal-button-div'>
        <button className='close' onClick={handleClose}>Fermer</button>
        <button className='valider' onClick={submit} disabled={isDisabled}>
          {loading ? 'Enregistrement...' : (isEditing ? 'Enregistrer' : 'Créer')}
        </button>
      </div>
    </Modal>
  )
}

export default CreatePostModal
